import {
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import {
  fetchAccountInteractionsApi,
  fetchAccountInteractionsSummaryApi,
  fetchAccountConversationsBootstrapApi,
  fetchInteractionTranscriptApi,
  fetchMicrosoftAdminConsentUrlApi,
  fetchMicrosoftConversationsAuthUrlApi,
  fetchMicrosoftConversationsStatusApi,
} from "../api/accountsConversationsApi";
import { showErrorFromMutation } from "@/shared/utils/toast";

export const useMicrosoftConversationsStatus = ({ enabled = true } = {}) => {
  return useQuery({
    queryKey: ["microsoftConversationsStatus"],
    queryFn: fetchMicrosoftConversationsStatusApi,
    enabled,
    staleTime: 30 * 1000,
  });
};

export const useConnectMicrosoftConversations = () => {
  return useMutation({
    mutationFn: fetchMicrosoftConversationsAuthUrlApi,
    onSuccess: (response) => {
      const url = response?.data?.url;
      if (url) {
        window.location.href = url;
      }
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error?.response?.data?.error || "Failed to start Microsoft consent"
      );
    },
  });
};

export const useEnableMicrosoftTranscripts = () => {
  return useMutation({
    mutationFn: (params) =>
      fetchMicrosoftConversationsAuthUrlApi({ ...params, scopeMode: "transcripts" }),
    onSuccess: (response) => {
      const url = response?.data?.url;
      if (url) {
        window.location.href = url;
      }
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error?.response?.data?.error || "Failed to enable transcript permissions"
      );
    },
  });
};

export const useMicrosoftAdminConsentUrl = () => {
  return useMutation({
    mutationFn: fetchMicrosoftAdminConsentUrlApi,
    onError: (error) => {
      showErrorFromMutation(
        error,
        error?.response?.data?.error || "Failed to get admin consent link"
      );
    },
  });
};

export const useAccountConversationsBootstrap = ({
  companyName = "",
  enabled = true,
}) => {
  return useQuery({
    queryKey: ["accountConversationsBootstrap", companyName],
    queryFn: ({ signal }) =>
      fetchAccountConversationsBootstrapApi({ companyName, signal }),
    enabled: !!companyName && enabled,
    staleTime: 30 * 1000,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.sync_status;
      return status === "queued" || status === "running" ? 2000 : false;
    },
  });
};

export const useFetchAccountInteractions = ({
  companyName = "",
  sourceType = null,
  contactId = null,
  limit = 50,
  enabled = true,
}) => {
  return useInfiniteQuery({
    queryKey: ["accountInteractions", companyName, sourceType, contactId, limit],
    queryFn: ({ pageParam }) =>
      fetchAccountInteractionsApi({
        companyName,
        sourceType,
        contactId,
        limit,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const itemCount = lastPage?.data?.interactions?.length ?? 0;
      return itemCount < limit ? undefined : pages.length * limit;
    },
    enabled: !!companyName && enabled,
  });
};

export const useFetchAccountInteractionsSummary = ({
  companyName = "",
  enabled = true,
  forceRefresh = false,
}) => {
  return useQuery({
    queryKey: ["accountInteractionsSummary", companyName, forceRefresh],
    queryFn: () =>
      fetchAccountInteractionsSummaryApi({ companyName, forceRefresh }),
    enabled: !!companyName && enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFetchInteractionTranscript = ({
  interactionId = "",
  enabled = false,
}) => {
  return useQuery({
    queryKey: ["interactionTranscript", interactionId],
    queryFn: () => fetchInteractionTranscriptApi({ interactionId }),
    enabled: !!interactionId && enabled,
  });
};
